import React, { createContext, useContext, useState, useCallback, useMemo } from "react";

type ValidationContextValue = {
  errorField: string | null;
  errorMessage: string | null;
  shakeKey: number;
  setError: (field: string, message: string) => void;
  clearError: () => void;
};

const ValidationContext = createContext<ValidationContextValue | null>(null);

export function ValidationProvider({ children }: { children: React.ReactNode }) {
  const [errorField, setErrorField] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [shakeKey, setShakeKey] = useState(0);

  const setError = useCallback((field: string, message: string) => {
    setErrorField(field);
    setErrorMessage(message);
    // Increment shake key to trigger animation
    setShakeKey((prev) => prev + 1);

    // Scroll to the error field
    setTimeout(() => {
      const element = document.querySelector(`[data-field="${field}"]`);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 50);
  }, []);

  const clearError = useCallback(() => {
    setErrorField(null);
    setErrorMessage(null);
  }, []);

  const value = useMemo(
    () => ({ errorField, errorMessage, shakeKey, setError, clearError }),
    [errorField, errorMessage, shakeKey, setError, clearError]
  );

  return (
    <ValidationContext.Provider value={value}>
      {children}
    </ValidationContext.Provider>
  );
}

export function useValidation() {
  const ctx = useContext(ValidationContext);
  if (!ctx) {
    throw new Error("useValidation must be used within ValidationProvider");
  }
  return ctx;
}

/**
 * Hook for question components to check if they have an error
 */
export function useFieldError(field: string) {
  const { errorField, shakeKey } = useValidation();
  return {
    hasError: errorField === field,
    shakeKey,
  };
}
